'use client';

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { MapProvider, useMap } from '@/context/MapContext';
import { FiltersProvider, useFilters } from '@/context/FiltersContext';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { StartupCard } from '@/components/map/StartupCard';
import { OnboardingModal } from '@/components/map/OnboardingModal';
import { MapToolbar } from '@/components/map/MapToolbar';
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { applyFilters } from '@/utils/helpers';
import { track } from '@/utils/analytics';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { MapChartHandle, LegendEntry } from '@/components/map/MapChart';
import type { Startup } from '@/types/data';

// Load Plotly chart client-side only
const MapChart = dynamic(() => import('@/components/map/MapChart'), { ssr: false });

const MapPageInner: React.FC = () => {
  const { startups } = useMap();
  const { filters, resetFilters, activeFilterCount } = useFilters();
  const [entries, setEntries] = useState<LegendEntry[]>([]);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const [selectedStartup, setSelectedStartup] = useState<Startup | null>(null);
  // pan is the natural default on touch (single-finger drag pans the map)
  // desktop users can switch to zoom/selection via MapToolbar
  const [dragMode, setDragMode] = useState<'pan' | 'zoom'>('pan');
  const [onboardingDismissed, setOnboardingDismissed] = useLocalStorage('brecha_onboarding_v1', false);
  const chartRef = useRef<MapChartHandle>(null);

  const filteredCount = useMemo(
    () => applyFilters(startups, filters).length,
    [startups, filters],
  );

  // Debounced search tracking — fires 600ms after the user stops typing
  useEffect(() => {
    if (!filters.searchQuery) return;
    const timer = setTimeout(() => {
      track({ type: 'search_performed', query: filters.searchQuery, results: filteredCount });
    }, 600);
    return () => clearTimeout(timer);
  }, [filters.searchQuery, filteredCount]);

  const onChartReady = useCallback((legendEntries: LegendEntry[], _clusterColors: Record<number, string>): void => {
    setEntries(legendEntries);
  }, []);

  const handleSectionToggle = useCallback((): void => {
    requestAnimationFrame(() => { chartRef.current?.resize(); });
  }, [chartRef]);

  const handleStartupSelect = useCallback((startup: Startup): void => {
    setSelectedStartup(startup);
  }, []);

  const toggle = useCallback((name: string): void => {
    chartRef.current?.toggle(name);
    setHiddenSeries(prev => {
      const next = new Set(prev);
      const action = next.has(name) ? 'show' : 'hide';
      track({ type: 'cluster_toggled', clusterName: name, action });
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const selectAll = useCallback((): void => {
    chartRef.current?.selectAll();
    setHiddenSeries(new Set());
    track({ type: 'all_clusters_shown' });
  }, []);

  const hideAll = useCallback((): void => {
    chartRef.current?.hideAll();
    setHiddenSeries(new Set(entries.map(e => e.name)));
    track({ type: 'all_clusters_hidden' });
  }, [entries]);

  const zoomToCluster = useCallback((name: string): void => {
    chartRef.current?.zoomToCluster(name);
  }, []);

  const hasActiveFilters = activeFilterCount > 0;
  const hasHiddenClusters = hiddenSeries.size > 0 && entries.length > 0;
  const showStatusBar = hasActiveFilters || hasHiddenClusters;
  const isEmpty = startups.length > 0 && filteredCount === 0;

  return (
    <div className="flex flex-col h-dvh overflow-hidden bg-bg">
      <Header showBackLink />

      <main id="main-content" className="flex flex-1 overflow-hidden min-h-0">
        <Sidebar
          entries={entries}
          hiddenSeries={hiddenSeries}
          onToggle={toggle}
          onZoom={zoomToCluster}
          onSelectAll={selectAll}
          onHideAll={hideAll}
          filteredCount={filteredCount}
          totalCount={startups.length}
          onSectionToggle={handleSectionToggle}
        />
        <div className="relative flex-1 min-w-0">
          <MapChart ref={chartRef} onReady={onChartReady} onStartupSelect={handleStartupSelect} selectedStartup={selectedStartup} />
          <MapToolbar
            dragMode={dragMode}
            onSetDragMode={(mode) => { setDragMode(mode); chartRef.current?.setDragMode(mode); }}
            onZoomIn={() => chartRef.current?.zoomIn()}
            onZoomOut={() => chartRef.current?.zoomOut()}
            onResetView={() => chartRef.current?.resetView()}
          />
          {/* Mobile toolbar — drag-mode toggle omitted (touch uses pinch natively);
              zoom-in/out + reset cover all practical needs on mobile */}
          <div
            className="desk:hidden absolute bottom-[72px] right-3 z-[97] flex flex-col gap-[3px] p-[4px] rounded-sm border border-border"
            style={{ backgroundColor: 'var(--bg-panel)' }}
          >
            {(
              [
                { label: 'Zoom in',    icon: <ZoomIn size={17} aria-hidden="true" />,    onClick: () => chartRef.current?.zoomIn() },
                { label: 'Zoom out',   icon: <ZoomOut size={17} aria-hidden="true" />,   onClick: () => chartRef.current?.zoomOut() },
                { label: 'Reset view', icon: <RotateCcw size={17} aria-hidden="true" />, onClick: () => chartRef.current?.resetView() },
              ] as const
            ).map(({ label, icon, onClick }, i) => (
              <React.Fragment key={label}>
                {i === 2 && <div className="h-px bg-border mx-[4px] my-[3px]" aria-hidden="true" />}
                <button
                  type="button"
                  onClick={onClick}
                  aria-label={label}
                  className="p-[10px] rounded-sm border border-transparent text-text-muted hover:text-text-secondary hover:bg-bg-raised transition-colors"
                >
                  {icon}
                </button>
              </React.Fragment>
            ))}
          </div>

          {showStatusBar && (
            <div aria-live="polite" className="absolute top-3 left-1/2 -translate-x-1/2 z-[50] flex items-center gap-3 px-3 py-[5px] rounded-[20px] border border-border text-[11px] text-text-secondary tracking-[0.03em] whitespace-nowrap" style={{ backgroundColor: 'var(--bg-panel)' }}>
              {hasActiveFilters && (
                <span>
                  <span className="text-text-primary">{filteredCount.toLocaleString()}</span>
                  <span className="text-text-muted"> / {startups.length.toLocaleString()} startups</span>
                </span>
              )}
              {hasHiddenClusters && (
                <span>
                  <span className="text-text-primary">{entries.length - hiddenSeries.size}</span>
                  <span className="text-text-muted"> / {entries.length} clusters</span>
                </span>
              )}
              <button
                type="button"
                onClick={() => { resetFilters(); selectAll(); }}
                className="text-text-muted hover:text-text-secondary transition-colors"
                aria-label="Clear all filters and show all clusters"
              >
                Clear all
              </button>
            </div>
          )}

          {isEmpty && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-bg/80 z-[50]">
              <p className="text-text-secondary text-[13px]">No startups match your current filters.</p>
              <button
                type="button"
                onClick={resetFilters}
                className="text-[11px] font-medium px-4 py-[6px] rounded-sm border border-accent text-accent hover:bg-accent-dim transition-colors tracking-[0.04em]"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </main>

      {selectedStartup && (
        <StartupCard
          startup={selectedStartup}
          onClose={() => setSelectedStartup(null)}
          clusterEntry={entries.find(e => e.name === selectedStartup.label_final) ?? null}
        />
      )}
      {!onboardingDismissed && (
        <OnboardingModal
          startupCount={startups.length}
          onDismiss={() => setOnboardingDismissed(true)}
        />
      )}
    </div>
  );
};

export default function MapPage(): React.ReactElement {
  return (
    <MapProvider>
      <FiltersProvider>
        <MapPageInner />
      </FiltersProvider>
    </MapProvider>
  );
}
